import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Injector,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { POPUP_DATA } from 'src/app/@shared/services/modal/modal.service';
import { MatDialogRef } from '@angular/material/dialog';
import { CardDesignComponent } from '../../slide-up-card/card-design.component';
import { ComponentType } from '@angular/cdk/portal';

@Component({
    selector: 'app-dialog',
    templateUrl: './dialog.component.html',
    styleUrls: ['./dialog.component.scss'],
    standalone: false
})
export class DialogComponent<T> implements AfterViewInit, OnDestroy {
  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef })
  container: ViewContainerRef;

  @ViewChild('trapContainer', { read: ElementRef })
  trapContainer?: ElementRef<HTMLElement>;

  public componentInjector: Injector;
  public componentRef: any;

  private focusTrap?: FocusTrap;
  private trapAttachTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    @Inject(POPUP_DATA) public popUpData: any,
    public readonly injector: Injector,
    private readonly ref: MatDialogRef<DialogComponent<T>>,
    private readonly focusTrapFactory: FocusTrapFactory,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.componentInjector = Injector.create({
      providers: [{ provide: POPUP_DATA, useValue: popUpData.data }],
      parent: this.injector,
    });
  }

  ngAfterViewInit(): void {
    this.loadComponent();
    this.cdr.detectChanges();
    // Defer so nested templates (*ngIf) inside the loaded popup are in the DOM before we scan tabbables.
    this.trapAttachTimeoutId = setTimeout(() => {
      this.trapAttachTimeoutId = undefined;
      this.attachFocusTrap();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.trapAttachTimeoutId != null) {
      clearTimeout(this.trapAttachTimeoutId);
    }
    this.focusTrap?.destroy();
    this.focusTrap = undefined;
  }

  /** Focus trap must run after dynamic content exists (Material trap can init too early). */
  private attachFocusTrap(): void {
    const el = this.trapContainer?.nativeElement;
    if (!el) {
      return;
    }
    this.focusTrap?.destroy();
    this.focusTrap = this.focusTrapFactory.create(el, false);
    void this.focusTrap.focusInitialElementWhenReady().catch(() => {
      /* no focusable element */
    });
  }

  get component(): ComponentType<T> {
    return this.popUpData.component;
  }

  closeDialog(result?: any) {
    this.ref.close(result);
  }

  private loadComponent() {
    if (this.component) {
      this.container.clear();
      this.componentRef = this.container.createComponent<T>(this.component, {
        injector: this.componentInjector,
      });

      const instance = this.componentRef.instance as CardDesignComponent;

      if (!!instance) {
        instance.onCardClose.subscribe((value) => this.closeDialog(value));
      }
    }
  }
}
